/**
 * IndexedDB cache for decoded height grids, keyed by (provider, bbox, cellSize)
 * per SPEC §3.1.
 *
 * Two reasons: it keeps panning back to a site instant, and it keeps us from
 * hammering swisstopo and IGN, whose open data we are using on trust.
 */

import type { HeightGrid, ProviderId } from './types';

const DB_NAME = 'terrain-airflow';
const STORE = 'dem';
const VERSION = 1;
const MAX_ENTRIES = 120;

interface CacheRecord {
  key: string;
  stored: number;
  provider: ProviderId;
  width: number;
  height: number;
  cellSize: number;
  origin: [number, number];
  crs: string;
  source: string;
  buffer: ArrayBuffer;
}

let dbPromise: Promise<IDBDatabase | null> | null = null;

function openDb(): Promise<IDBDatabase | null> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve) => {
    if (typeof indexedDB === 'undefined') return resolve(null);
    const req = indexedDB.open(DB_NAME, VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: 'key' });
        store.createIndex('stored', 'stored');
      }
    };
    req.onsuccess = () => resolve(req.result);
    // A blocked or unavailable IndexedDB must never break DEM loading.
    req.onerror = () => resolve(null);
  });
  return dbPromise;
}

export function cacheKey(
  provider: ProviderId,
  bbox: [number, number, number, number],
  cellSize: number
): string {
  const b = bbox.map((v) => v.toFixed(5)).join(',');
  return `${provider}|${b}|${cellSize.toFixed(2)}`;
}

export async function readCache(key: string): Promise<HeightGrid | null> {
  const db = await openDb();
  if (!db) return null;
  return new Promise((resolve) => {
    const tx = db.transaction(STORE, 'readonly');
    const req = tx.objectStore(STORE).get(key);
    req.onsuccess = () => {
      const rec = req.result as CacheRecord | undefined;
      if (!rec) return resolve(null);
      resolve({
        data: new Float32Array(rec.buffer),
        width: rec.width,
        height: rec.height,
        cellSize: rec.cellSize,
        origin: rec.origin,
        crs: rec.crs,
        source: rec.source,
      });
    };
    req.onerror = () => resolve(null);
  });
}

export async function writeCache(
  key: string,
  provider: ProviderId,
  grid: HeightGrid
): Promise<void> {
  const db = await openDb();
  if (!db) return;
  const rec: CacheRecord = {
    key,
    stored: Date.now(),
    provider,
    width: grid.width,
    height: grid.height,
    cellSize: grid.cellSize,
    origin: grid.origin,
    crs: grid.crs,
    source: grid.source,
    // Copy: the caller keeps using the original, and structured clone of a
    // subarray view would serialise the whole parent buffer.
    buffer: grid.data.slice().buffer,
  };
  await new Promise<void>((resolve) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).put(rec);
    tx.oncomplete = () => resolve();
    tx.onerror = () => resolve();
  });
  void evictOldest(db);
}

/** Keep the store bounded; DEM grids are ~1 MB each at 512². */
async function evictOldest(db: IDBDatabase): Promise<void> {
  return new Promise((resolve) => {
    const tx = db.transaction(STORE, 'readwrite');
    const store = tx.objectStore(STORE);
    const countReq = store.count();
    countReq.onsuccess = () => {
      const excess = countReq.result - MAX_ENTRIES;
      if (excess <= 0) return resolve();
      let removed = 0;
      const cursorReq = store.index('stored').openCursor();
      cursorReq.onsuccess = () => {
        const cursor = cursorReq.result;
        if (!cursor || removed >= excess) return resolve();
        cursor.delete();
        removed++;
        cursor.continue();
      };
      cursorReq.onerror = () => resolve();
    };
    countReq.onerror = () => resolve();
  });
}

export async function clearCache(): Promise<void> {
  const db = await openDb();
  if (!db) return;
  await new Promise<void>((resolve) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).clear();
    tx.oncomplete = () => resolve();
    tx.onerror = () => resolve();
  });
}
